import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import AppSwitcher from "../components/AppSwitcher";
import { API_URL, refreshAllLiveData } from "./api";

const NAV = [
  { to: "/orchestration", label: "Overview", end: true },
  { to: "/api/work-orders", label: "Work Orders", end: true },
  { to: "/api/runs", label: "Tasks", end: true },
  { to: "/api/review", label: "Reviews", end: false },
  { to: "/api/config", label: "LLM Config", end: false },
  { to: "/api/admin", label: "Admin", end: false },
] as const;

function breadcrumb(pathname: string): string[] {
  if (pathname === "/orchestration") return ["Overview"];
  if (pathname.startsWith("/api/work-orders")) return ["Work Orders"];
  if (pathname === "/api/runs") return ["Tasks"];
  if (pathname.startsWith("/api/config")) return ["LLM Config"];
  if (pathname.startsWith("/api/review")) return ["Reviews"];
  if (pathname.startsWith("/api/admin")) return ["Admin"];
  if (pathname.startsWith("/api/runs/")) {
    const id = pathname.split("/").pop() ?? "";
    return ["Tasks", id.length > 8 ? `${id.slice(0, 8)}…` : id];
  }
  return ["DAG Brain"];
}

const ControlPlaneLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { instance, accounts } = useMsal();
  const account = accounts[0] || instance.getActiveAccount();
  const userEmail = account?.username || account?.idTokenClaims?.preferred_username;
  const crumbs = breadcrumb(pathname);
  const apiHost = API_URL.replace(/^https?:\/\//, "");

  return (
    <div className="flex h-screen bg-[#0d0d0a] text-[#E5E0D5] overflow-hidden">
      <aside className="w-52 flex-shrink-0 bg-[#111110] flex flex-col border-r border-[#1f1e1b]">
        <div className="px-3 py-3 border-b border-[#1f1e1b]">
          <AppSwitcher currentAppId="orchestration" />
        </div>

        <nav className="flex-1 py-3 text-sm">
          <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
            Control Plane
          </div>
          {NAV.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `block mx-2 px-3 py-2 border-l-2 transition-colors ${
                  isActive
                    ? "text-[#C9A260] border-[#C9A260] bg-[#C9A260]/8"
                    : "text-[#9D9585] border-transparent hover:text-[#E5E0D5] hover:bg-white/4"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-3 border-t border-[#1f1e1b] text-xs space-y-2">
          {userEmail && (
            <div className="truncate text-[#5A5448]" title={userEmail}>
              {userEmail}
            </div>
          )}
          <button
            type="button"
            onClick={() =>
              instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
            }
            className="text-[#5A5448] hover:text-[#C9A260] inline-flex items-center gap-1 transition-colors"
          >
            ↩ Sign out
          </button>
          <div className="font-mono truncate text-[#3a3830]" title={API_URL}>
            {apiHost}
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-11 flex-shrink-0 bg-[#111110] border-b border-[#1f1e1b] flex items-center px-4 gap-2 text-sm">
          <span className="text-[#3a3830]">/</span>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="text-[#3a3830]">/</span>}
              <span
                className={
                  i === crumbs.length - 1
                    ? "font-mono text-[#E5E0D5]"
                    : "text-[#5A5448]"
                }
              >
                {c}
              </span>
            </span>
          ))}
          <div className="ml-auto flex items-center gap-3 text-xs text-[#5A5448]">
            <span>auto-refresh 60s</span>
            <button
              type="button"
              onClick={() => refreshAllLiveData()}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 border border-[#262420] text-[#9D9585] hover:border-[#C9A260] hover:text-[#C9A260] transition-colors"
              title="Refresh all live data now"
            >
              ↻ Refresh
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ControlPlaneLayout;
